if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
        this.addEventListener('click', this.openReviews.bind(this), true);
        this.addEventListener('keydown', (event) => {
          if (event.key === 'Enter' || event.key === ' ') this.openReviews(event);
        }, true);

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.reviewObserver?.disconnect();
        this.reviewResizeObserver?.disconnect();
        clearTimeout(this.reviewTimeout);
        this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');

        fetch(opener.getAttribute('data-product-url'))
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');

            this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);
            this.loadReviews(responseHTML, opener.getAttribute('data-product-url'));

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            this.modalContent.querySelector('product-component')?.dispatchViewEvent?.();

            super.show(opener);
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      openReviews(event) {
        if (!event.target.closest('.jdgm-preview-badge, .judgeme-preview-badge')) return;
        const reviews = this.modalContent.querySelector('.quick-add-reviews');
        if (!reviews) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        const heading = reviews.querySelector('h2');
        heading.focus({ preventScroll: true });
        const scroller = this.modalContent.scrollHeight > this.modalContent.clientHeight
          ? this.modalContent : this.querySelector('.quick-add-modal__content');
        scroller.scrollTo({
          top: scroller.scrollTop + heading.getBoundingClientRect().top - scroller.getBoundingClientRect().top - 20,
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
        });
      }

      loadReviews(responseHTML, productUrl) {
        const reviews = document.createElement('section');
        reviews.className = 'quick-add-reviews';
        const heading = document.createElement('h2');
        heading.id = `${this.id}-reviews`;
        heading.tabIndex = -1;
        heading.textContent = 'Customer reviews';
        reviews.setAttribute('aria-labelledby', heading.id);
        const status = document.createElement('p');
        status.setAttribute('role', 'status');
        status.textContent = 'Loading reviews…';
        const link = document.createElement('a');
        link.href = `${productUrl.split('#')[0]}#judgeme_product_reviews`;
        link.textContent = 'View reviews on the product page';
        reviews.append(heading, status, link);
        this.modalContent.append(reviews);

        const widget = responseHTML.querySelector('.jdgm-review-widget');
        const data = responseHTML.querySelector('script.jdgm-review-widget-data');
        const loader = Array.from(responseHTML.querySelectorAll('script[src]')).find((script) =>
          /\/judgeme-[^/]+\/assets\/loader\.js/.test(script.src)
        );
        const count = responseHTML.querySelector('.jdgm-prev-badge')?.dataset.numberOfReviews;
        if (count === '0' || (widget && !data && widget.children.length === 0)) {
          status.textContent = 'No reviews for this product yet.';
          return;
        }
        if (!widget || !data || !loader) {
          status.textContent = 'Reviews are available on the product page.';
          return;
        }

        // Judge.me's current module scans once at startup; a fresh document also owns its cleanup.
        const frameDocument = document.implementation.createHTMLDocument('Customer reviews');
        frameDocument.documentElement.lang = document.documentElement.lang;
        const charset = frameDocument.createElement('meta');
        charset.setAttribute('charset', 'utf-8');
        frameDocument.head.prepend(charset);
        const base = frameDocument.createElement('base');
        base.href = window.location.origin;
        frameDocument.head.append(base);
        responseHTML.head.querySelectorAll('style, script').forEach((node) => {
          if (node.matches('.jdgm-settings-script, .jdgm-script') ||
              (node.tagName === 'STYLE' && node.textContent.includes('.jdgm-'))) {
            frameDocument.head.append(node.cloneNode(true));
          }
        });
        const settings = frameDocument.createElement('script');
        settings.textContent = `window.Shopify = ${JSON.stringify({ shop: window.Shopify?.shop, locale: window.Shopify?.locale }).replaceAll('<', '\\u003c')};
          jdgmSettings.disable_web_reviews = true;
          jdgmSettings.widget_web_restriction_product_reviews_text = 'No reviews for this product yet.';`;
        frameDocument.head.append(settings);
        const style = frameDocument.createElement('style');
        const theme = getComputedStyle(this.modalContent);
        style.textContent = `html { background: transparent; } body { margin: 0; color: ${theme.color}; font-family: ${theme.fontFamily}; }
          *, *::before, *::after { box-sizing: border-box; }
          .jdgm-review-widget { max-width: 100% !important; }
          .jdgm-review-widget h2 { display: none; }
          img, video { max-width: 100%; }
          .jdgm-write-rev-link, .jdgm-form-wrapper, .jdgm-ask-question-btn,
          [data-testid="write-review-button"], [data-testid="ask-question-button"],
          .jm-action-buttons, .jm-no-reviews-state__action-buttons { display: none !important; }`;
        frameDocument.head.append(style, loader.cloneNode(true));
        const widgetClone = widget.cloneNode(true);
        widgetClone.dataset.emptyState = '';
        frameDocument.body.append(widgetClone, data.cloneNode(true));
        const frame = document.createElement('iframe');
        frame.className = 'quick-add-reviews__frame';
        frame.title = `Customer reviews for ${widget.dataset.productTitle || 'this product'}`;
        frame.style.visibility = 'hidden';
        frame.addEventListener('load', () => {
          if (!frame.isConnected) return;
          const doc = frame.contentDocument;
          const update = () => {
            if (!frame.isConnected) return;
            frame.style.height = `${Math.max(180, Math.ceil(doc.body.getBoundingClientRect().height)) + 8}px`;
            if (doc.querySelector('.jdgm-widget-revamp')?.childElementCount) {
              status.hidden = true;
              frame.hidden = false;
              frame.style.visibility = '';
              clearTimeout(this.reviewTimeout);
            }
          };
          this.reviewObserver = new MutationObserver(update);
          this.reviewObserver.observe(doc.body, { childList: true, subtree: true });
          this.reviewResizeObserver = new ResizeObserver(update);
          this.reviewResizeObserver.observe(doc.body);
          doc.addEventListener('keydown', (event) => {
            const photoOpen = Array.from(doc.querySelectorAll('[role="dialog"], .jdgm-modal__container'))
              .some((dialog) => dialog.getClientRects().length > 0);
            if (event.key === 'Escape' && !photoOpen) {
              event.preventDefault();
              this.hide();
            }
          });
          update();
        });
        frame.srcdoc = frameDocument.documentElement.outerHTML;
        reviews.insertBefore(frame, link);
        this.reviewTimeout = setTimeout(() => {
          if (!frame.isConnected) return;
          status.textContent = 'Unable to load reviews here. Please use the product page link below.';
          status.hidden = false;
          frame.hidden = true;
        }, 15000);
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}
